import {Component, computed, signal, Signal} from '@angular/core';

import {PopoverModule} from 'primeng/popover';

import {DataBatchTools} from '@selpia/shared/utils';
import {Alignment, DataChunk, DataPointParams} from '@selpia/shared/models';
import {
  DataChunkTitlePipe,
  DatapointMatchesPipe,
  DirectionIndicatorComponent,
  DrilldownComponent,
  GetDataPointPipe,
  L12mLineChartComponent,
  ListTableComponent,
  ReportButtonComponent,
} from '@selpia/data-batch/ui';
import {FormatByMeasurePipe} from '@selpia/measures/pipes';
import {BarMinichartComponent, OutsideClickDirective, SvgSpriteIcon} from '@selpia/shared/ui';
import {TilePreviewComponent} from '../tile-preview';

@Component({
  selector: 'lib-feature-tile-list',
  templateUrl: './feature-tile-list.component.html',
  styleUrl: './feature-tile-list.component.css',
  imports: [
    DataChunkTitlePipe,
    GetDataPointPipe,
    FormatByMeasurePipe,
    DirectionIndicatorComponent,
    ListTableComponent,
    PopoverModule,
    DrilldownComponent,
    ReportButtonComponent,
    BarMinichartComponent,
    OutsideClickDirective,
    DatapointMatchesPipe,
    L12mLineChartComponent,
  ],
})
export class FeatureTileListComponent extends TilePreviewComponent {
  protected readonly pyParams: Signal<DataPointParams> = computed(() => {
    const measure = this.focusMeasure();

    return {
      measureCode: measure ? measure.code + '_PY' : undefined,
      periodCode: this.focusPeriod(),
      date: this.focusDate(),
    };
  });

  protected readonly impactParams: Signal<DataPointParams> = computed(() => {
    return {
      measureCode: this.focusMeasure()?.impactMeasureCode,
      periodCode: this.focusPeriod(),
      date: this.focusDate(),
    };
  });

  protected readonly absoluteGrowthParams: Signal<DataPointParams> = computed(() => {
    return {
      measureCode: this.focusMeasure()?.growthMeasureAbsoluteCode,
      periodCode: this.focusPeriod(),
      date: this.focusDate(),
    };
  });

  protected readonly absoluteMinMax: Signal<{ min: number, max: number } | undefined> = computed(() => {
    const dataChunks = this.dataBatchFacade.dataChunks();
    if (!dataChunks) {
      return undefined;
    }

    return DataBatchTools.getMinMax(dataChunks, this.absoluteGrowthParams());
  });

  protected readonly relativeGrowthParams: Signal<DataPointParams> = computed(() => {
    return {
      measureCode: this.focusMeasure()?.growthMeasureRelativeCode,
      periodCode: this.focusPeriod(),
      date: this.focusDate(),
    };
  });

  protected readonly relativeMinMax: Signal<{ min: number, max: number } | undefined> = computed(() => {
    const dataChunks = this.dataBatchFacade.dataChunks();
    if (!dataChunks) {
      return undefined;
    }

    return DataBatchTools.getMinMax(dataChunks, this.relativeGrowthParams());
  });

  protected readonly selectedDataChunk = signal<DataChunk | null>(null);

  selectDataChunk(chunk: DataChunk) {
    if (chunk === this.selectedDataChunk()) {
      this.selectedDataChunk.set(null);
    } else {
      this.selectedDataChunk.set(chunk);
    }
  }

  dismissSelected() {
    this.selectedDataChunk.set(null);
  }

  protected measureCode = computed(() => this.focusMeasure()?.code);
  protected readonly valueRange = computed(() => {
      const dataChunks = this.filteredDataChunks();
      if (dataChunks) {
        return this.getMinMax(this.measureCode(), this.focusPeriod(), dataChunks);
      }
      return;
    },
  );

  protected readonly SvgSpriteIcon = SvgSpriteIcon;
  protected readonly Alignment = Alignment;
}
